import React from "react";
import { ComponentConfig } from "@measured/puck";

// Spacer Component
export const Spacer: ComponentConfig<{
  height: number;
}> = {
  fields: {
    height: { type: "number", label: "Height (px)", min: 0, max: 500 },
  },
  defaultProps: {
    height: 50,
  },
  render: ({ height }) => {
    return <div style={{ height: `${height}px` }} />;
  },
};

// Text Component
export const Text: ComponentConfig<{
  content: string;
  tag: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span";
  fontSize: number;
  fontWeight: number;
  textAlign: "left" | "center" | "right" | "justify";
  color: string;
}> = {
  fields: {
    content: { type: "textarea", label: "Content" },
    tag: {
      type: "select",
      label: "Tag",
      options: [
        { label: "H1", value: "h1" },
        { label: "H2", value: "h2" },
        { label: "H3", value: "h3" },
        { label: "H4", value: "h4" },
        { label: "H5", value: "h5" },
        { label: "H6", value: "h6" },
        { label: "Paragraph", value: "p" },
        { label: "Span", value: "span" },
      ],
    },
    fontSize: { type: "number", label: "Font Size (px)", min: 8, max: 72 },
    fontWeight: { type: "number", label: "Font Weight", min: 100, max: 900, step: 100 },
    textAlign: {
      type: "select",
      label: "Text Align",
      options: [
        { label: "Left", value: "left" },
        { label: "Center", value: "center" },
        { label: "Right", value: "right" },
        { label: "Justify", value: "justify" },
      ],
    },
    color: { type: "text", label: "Text Color" },
  },
  defaultProps: {
    content: "Text content",
    tag: "p",
    fontSize: 16,
    fontWeight: 400,
    textAlign: "left",
    color: "#000000",
  },
  render: ({ content, tag, fontSize, fontWeight, textAlign, color }) => {
    const Tag = tag as keyof JSX.IntrinsicElements;
    return (
      <Tag
        style={{
          fontSize: `${fontSize}px`,
          fontWeight,
          textAlign,
          color,
          maxWidth: "1280px",
          margin: "0 auto",
          padding: "0 40px",
        }}
      >
        {content}
      </Tag>
    );
  },
};

// Button Component
export const Button: ComponentConfig<{
  text: string;
  link: string;
  variant: "primary" | "secondary" | "outline" | "ghost";
  size: "sm" | "md" | "lg";
  fullWidth: boolean;
  backgroundColor: string;
  textColor: string;
}> = {
  fields: {
    text: { type: "text", label: "Button Text" },
    link: { type: "text", label: "Link" },
    variant: {
      type: "select",
      label: "Variant",
      options: [
        { label: "Primary", value: "primary" },
        { label: "Secondary", value: "secondary" },
        { label: "Outline", value: "outline" },
        { label: "Ghost", value: "ghost" },
      ],
    },
    size: {
      type: "select",
      label: "Size",
      options: [
        { label: "Small", value: "sm" },
        { label: "Medium", value: "md" },
        { label: "Large", value: "lg" },
      ],
    },
    fullWidth: { type: "select" as const, label: "Full Width", options: [{ label: "Yes", value: true }, { label: "No", value: false }] },
    backgroundColor: { type: "text", label: "Background Color" },
    textColor: { type: "text", label: "Text Color" },
  },
  defaultProps: {
    text: "Button",
    link: "#",
    variant: "primary",
    size: "md",
    fullWidth: false,
    backgroundColor: "#17265a",
    textColor: "#ffffff",
  },
  render: ({ text, link, variant, size, fullWidth, backgroundColor, textColor }) => {
    const getVariantStyles = () => {
      switch (variant) {
        case "primary":
          return {
            backgroundColor: backgroundColor || "#17265a",
            color: textColor || "#ffffff",
            border: "none",
          };
        case "secondary":
          return {
            backgroundColor: "#6b7280",
            color: "#ffffff",
            border: "none",
          };
        case "outline":
          return {
            backgroundColor: "transparent",
            color: backgroundColor || "#17265a",
            border: `2px solid ${backgroundColor || "#17265a"}`,
          };
        case "ghost":
          return {
            backgroundColor: "transparent",
            color: backgroundColor || "#17265a",
            border: "none",
          };
        default:
          return {
            backgroundColor: backgroundColor || "#17265a",
            color: textColor || "#ffffff",
            border: "none",
          };
      }
    };

    const getSizeStyles = () => {
      switch (size) {
        case "sm":
          return { padding: "8px 16px", fontSize: "14px" };
        case "lg":
          return { padding: "16px 32px", fontSize: "18px" };
        default:
          return { padding: "12px 24px", fontSize: "16px" };
      }
    };

    return (
      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 40px" }}>
        <a
          href={link}
          style={{
            display: fullWidth ? "block" : "inline-block",
            width: fullWidth ? "100%" : "auto",
            textAlign: "center",
            borderRadius: "8px",
            textDecoration: "none",
            fontWeight: 600,
            transition: "opacity 0.2s",
            ...getVariantStyles(),
            ...getSizeStyles(),
          }}
        >
          {text}
        </a>
      </div>
    );
  },
};

// Divider Component
export const Divider: ComponentConfig<{
  color: string;
  thickness: number;
  style: "solid" | "dashed" | "dotted";
}> = {
  fields: {
    color: { type: "text", label: "Color" },
    thickness: { type: "number", label: "Thickness (px)", min: 1, max: 10 },
    style: {
      type: "select",
      label: "Style",
      options: [
        { label: "Solid", value: "solid" },
        { label: "Dashed", value: "dashed" },
        { label: "Dotted", value: "dotted" },
      ],
    },
  },
  defaultProps: {
    color: "#e5e7eb",
    thickness: 1,
    style: "solid",
  },
  render: ({ color, thickness, style }) => {
    return (
      <div style={{ maxWidth: "1280px", margin: "40px auto", padding: "0 40px" }}>
        <hr
          style={{
            border: "none",
            borderTop: `${thickness}px ${style} ${color}`,
            margin: 0,
          }}
        />
      </div>
    );
  },
};

